import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaTimesCircle, FaUserMd, FaClock, FaInfoCircle } from 'react-icons/fa';
import './WaitingListPage.css';
import { getApiBase, getAuthServerBase } from '../utils/runtime';
import { useLanguage } from '../i18n/LanguageContext';
import { formatCareLabel } from '../care/labels';

const AUTH_SERVER = getAuthServerBase();
const API_URL = getApiBase();

function WaitingListPage() {
  const navigate = useNavigate();
  const { language, isEnglish } = useLanguage();
  const [requests, setRequests] = useState([]);
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [user, setUser] = useState(null);
  const [message, setMessage] = useState('');
  const locale = isEnglish ? 'en-CA' : 'fr-CA';

  useEffect(() => {
    fetch(`${AUTH_SERVER}/me`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error(isEnglish ? 'Not authenticated' : 'Non authentifie');
        return res.json();
      })
      .then(data => {
        setUser(data);
      })
      .catch(() => navigate('/connexion'));
  }, [isEnglish, navigate]);

  useEffect(() => {
    if (user?.sub) {
      loadRequests();
      loadServices();
      loadProfessionals();
    }
  }, [user]);

  const loadRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/waiting-list/${user.sub}`);
      const data = await res.json();
      const sorted = data.sort((a, b) => {
        const statusOrder = { pending: 0, notified: 1, accepted: 2, cancelled: 3 };
        const aStatus = statusOrder[a.status] ?? 99;
        const bStatus = statusOrder[b.status] ?? 99;
        if (aStatus !== bStatus) return aStatus - bStatus;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setRequests(sorted);
    } catch (err) {
      console.error('Error loading waitlist requests', err);
    }
  };

  const loadServices = async () => {
    try {
      const res = await fetch(`${API_URL}/services`);
      const data = await res.json();
      setServices(data);
    } catch (err) {
      console.error('Error loading services', err);
    }
  };

  const loadProfessionals = async () => {
    try {
      const res = await fetch(`${API_URL}/prestataires`);
      const data = await res.json();
      setProfessionals(data);
    } catch (err) {
      console.error('Error loading professionals', err);
    }
  };

  const getServiceName = id => services.find(s => s.service_id === id)?.name || id;
  const getProfessionalName = id => {
    const pro = professionals.find(p => p.user_id === id);
    return pro ? `${pro.first_name} ${pro.last_name}` : '—';
  };

  const formatDateTime = (date, time) => {
    if (!date || !time) return null;
    return new Date(`${date}T${time}`).toLocaleString(locale, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const handleCancel = async (id) => {
    if (!window.confirm(isEnglish ? 'Do you want to cancel this request?' : 'Voulez-vous annuler cette demande ?')) return;
    try {
      const res = await fetch(`${API_URL}/waiting-list/${id}/cancel`, { method: 'PUT' });
      if (!res.ok) throw new Error();
      setMessage(isEnglish ? 'Your request was cancelled.' : 'Demande annulee avec succes');
      await loadRequests();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Error cancelling waitlist request', err);
      setMessage(isEnglish ? 'There was a problem cancelling this request.' : 'Erreur lors de l annulation');
    }
  };

  if (!user) return <p className="waitinglist-loading">{isEnglish ? 'Loading your waitlist...' : 'Chargement...'}</p>;

  return (
    <div className="waitinglist-container">
     {/* <button className="btn-back" onClick={() => navigate('/patient/dashboard')}>
        ← Retour
      </button>*/}
      <h2 className="waitinglist-title">{isEnglish ? 'My waitlist' : 'Ma liste d attente'}</h2>

      {message && <div className="waitinglist-message">{message}</div>}

      {requests.length > 0 ? (
        <ul className="waitinglist-list">
          {requests.map((req) => (
            <li key={req.request_id} className={`waitinglist-item status-${req.status}`}>
              <p><FaInfoCircle /> <strong>{getServiceName(req.service_id)}</strong> {req.professional_id && <>{isEnglish ? 'with' : 'avec'} <strong>{getProfessionalName(req.professional_id)}</strong></>}</p>
              <p><FaCalendarAlt /> {isEnglish ? 'Requested on:' : 'Demande le :'} {new Date(req.created_at).toLocaleString(locale)}</p>
              {req.date && req.start_time && (
                <p><FaClock /> {isEnglish ? 'Requested time:' : 'Souhaite :'} <strong>{formatDateTime(req.date, req.start_time)}</strong> {req.mode === 'virtual' ? (isEnglish ? '(virtual)' : '(en ligne)') : req.mode === 'in_person' ? (isEnglish ? '(in home)' : '(domicile)') : ''}</p>
              )}
              <p className="status-label">{isEnglish ? 'Status:' : 'Statut :'} <strong>{formatCareLabel(req.status, language)}</strong></p>
              {req.status === 'pending' && (
                <button className="btn-cancel" onClick={() => handleCancel(req.request_id)} disabled={!!message}>
                  <FaTimesCircle /> {isEnglish ? 'Cancel request' : 'Annuler'}
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="waitinglist-empty">{isEnglish ? 'You do not have any active waitlist requests.' : 'Vous n avez aucune demande en attente.'}</p>
      )}
    </div>
  );
}

export default WaitingListPage;
